<script setup lang="ts">
import { useEmployee } from '@/composables/useEmployee';
import type { WorkingPeriod } from '@/types/employee';
import { formatDateTime, formatTime } from '@/utils/time';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import { useToast } from 'primevue/usetoast';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  visible: boolean;
  workingPeriod?: WorkingPeriod | null;
  restaurantUserId: number;
}

interface Emits {
  (e: 'update:visible', value: boolean): void;
  (e: 'deleted', workingPeriodId: number): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const { t } = useI18n();
const toast = useToast();
const { deleteWorkingPeriod, isLoading } = useEmployee();

// Computed properties
const periodDetails = computed(() => {
  if (!props.workingPeriod) return '';

  const start = formatDateTime(props.workingPeriod.start_time);
  const end = props.workingPeriod.end_time ? formatDateTime(props.workingPeriod.end_time) : t('employee.timeclock.onGoing');

  return `${start} - ${end}`;
});

// Methods
const handleDelete = async () => {
  if (!props.workingPeriod) return;

  try {
    const { error } = await deleteWorkingPeriod(props.restaurantUserId, props.workingPeriod.id);

    if (error) {
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: error as string,
        life: 3000,
      });
      return;
    }

    toast.add({
      severity: 'success',
      summary: t('common.success'),
      detail: t('employee.timeclock.entryDeleted'),
      life: 3000,
    });

    emit('deleted', props.workingPeriod.id);
    handleClose();
  } catch (error) {
    console.error('Error deleting working period:', error);
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('employee.timeclock.deleteFailed'),
      life: 3000,
    });
  }
};

const handleClose = () => {
  emit('update:visible', false);
};
</script>

<template>
  <Dialog :visible="visible" :header="t('employee.timeclock.deleteEntry')" modal class="w-[90vw] max-w-md" @update:visible="handleClose">
    <div class="flex flex-col gap-4">
      <p>{{ t('employee.timeclock.deleteConfirmation') }}</p>

      <div class="rounded border bg-gray-50 p-3">
        <div class="mb-1 text-sm text-gray-600">{{ t('employee.timeclock.entryDetails') }}:</div>
        <div class="font-medium">
          {{ periodDetails }}
        </div>
        <div v-if="workingPeriod?.duration_minutes" class="mt-1 text-sm text-gray-600">
          {{ t('employee.timeclock.duration') }}: {{ formatTime(workingPeriod.duration_minutes * 60) }}
        </div>
      </div>

      <p class="text-sm text-red-600">
        {{ t('employee.timeclock.deleteWarning') }}
      </p>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button :label="t('common.cancel')" severity="secondary" outlined @click="handleClose" />
        <Button :label="t('common.delete')" severity="danger" :loading="isLoading" @click="handleDelete" />
      </div>
    </template>
  </Dialog>
</template>
